import PageShell from "@/components/site/PageShell";
import SEO from "@/components/site/SEO";
import JsonLd, { buildBreadcrumbs } from "@/components/site/JsonLd";
import TrustBand from "@/components/site/TrustBand";
import Fleet from "@/components/site/Fleet";
import { Award, Building, MapPinned, Users, ShieldCheck, FileCheck2, Satellite, Wrench } from "lucide-react";

const MILESTONES = [
  { v: "15+", l: "Años operando" },
  { v: "1.200+", l: "Grupos movidos" },
  { v: "32", l: "Departamentos" },
  { v: "98%", l: "Clientes que vuelven" },
];

const CREDENTIALS = [
  { icon: Building, t: "Empresa constituida", d: "Líneas Doradas S.A.S. registrada legalmente en Colombia." },
  { icon: Award, t: "Conductores certificados", d: "Todos con licencia C2/C3 vigente y curso pedagógico." },
  { icon: MapPinned, t: "Cobertura nacional", d: "32 departamentos con vehículos verificados." },
  { icon: Users, t: "Grupos desde 5 pax", d: "Van, buseta o bus según el número de pasajeros." },
  { icon: ShieldCheck, t: "SOAT y pólizas al día", d: "Todos los vehículos con SOAT vigente y póliza de responsabilidad civil contractual y extracontractual." },
  { icon: FileCheck2, t: "Antecedentes verificados", d: "Cada conductor pasa por verificación de antecedentes judiciales antes de vincularse." },
  { icon: Satellite, t: "GPS en tiempo real", d: "Monitoreo satelital de cada vehículo durante todo el trayecto." },
  { icon: Wrench, t: "Mantenimiento constante", d: "Revisión tecnomecánica al día y mantenimiento preventivo cada 2 meses." },
];

export default function Nosotros() {
  return (
    <PageShell testid="page-nosotros">
      <SEO
        title="Sobre Líneas Doradas S.A.S | Transporte Turístico Certificado"
        description="Conoce a Líneas Doradas S.A.S, empresa de transporte turístico con cobertura en Bogotá y toda Colombia, y conductores certificados."
      />
      <JsonLd
        id="breadcrumb-jsonld"
        data={buildBreadcrumbs([
          { name: "Inicio", url: "https://www.transporteenvanesybuses.com/" },
          { name: "Nosotros", url: "https://www.transporteenvanesybuses.com/nosotros" },
        ])}
      />

      <section className="relative pt-16 md:pt-24 pb-16 md:pb-20">
        <div className="orb-gold w-[520px] h-[520px] left-[-140px] top-[100px]" />
        <div className="orb-gold w-[420px] h-[420px] right-[-100px] top-[240px] opacity-70" />
        <div className="max-w-7xl mx-auto px-5 md:px-8 relative grid lg:grid-cols-[1.1fr_1fr] gap-14 items-center">
          <div>
            <div className="text-[#E8C87A] text-xs uppercase tracking-[0.35em] mb-4">Nosotros</div>
            <h1 className="font-display text-4xl md:text-6xl leading-[1.05] text-[#F5EFE0]">
              Sobre <span className="italic text-gold-grad">Líneas Doradas S.A.S.</span>
            </h1>
            <p className="text-[#F5EFE0]/70 mt-5 max-w-xl text-base md:text-lg leading-relaxed">
              Somos una empresa colombiana de transporte turístico y ejecutivo con más de 15 años moviendo grupos por todo el país. Nacimos en Bogotá con una premisa clara: <em className="text-[#E8C87A] not-italic font-semibold">llevar personas con puntualidad, seguridad y detalle.</em>
            </p>
            <p className="text-[#F5EFE0]/65 mt-4 max-w-xl leading-relaxed">
              Hoy operamos van, buseta y bus para agencias, empresas, colegios y familias que quieren moverse por Colombia sin sobresaltos. Cada servicio es coordinado por un humano — un WhatsApp, un solo interlocutor, y una promesa de excelencia dorada.
            </p>
          </div>
          <img src="/logo-lineas-doradas.webp" alt="Logo Líneas Doradas" className="w-full max-w-[380px] mx-auto opacity-95 drop-shadow-[0_0_50px_rgba(212,175,55,0.4)]" />
        </div>
      </section>

      <section className="py-20 md:py-24 border-t border-[#3A3229]/60">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-14">
            {MILESTONES.map((m) => (
              <div key={m.l} className="card-lux p-5 text-center">
                <div className="font-display text-4xl md:text-5xl text-gold-grad leading-none">{m.v}</div>
                <div className="text-[10px] uppercase tracking-widest text-[#F5EFE0]/55 mt-3">{m.l}</div>
              </div>
            ))}
          </div>

          <div className="max-w-2xl mb-10">
            <div className="text-[#E8C87A] text-xs uppercase tracking-[0.3em] mb-4">Credenciales</div>
            <h2 className="font-display text-3xl md:text-4xl text-[#F5EFE0]">Seguridad y confianza, verificables</h2>
            <p className="text-[#F5EFE0]/60 mt-3 text-sm">No solo lo decimos — así lo garantizamos en cada viaje.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {CREDENTIALS.map((c) => {
              const I = c.icon;
              return (
                <div key={c.t} className="p-5 rounded-xl border border-[#3A3229] hover:border-[#D4AF37]/50"
                     style={{ transitionProperty: "border-color", transitionDuration: "220ms" }}>
                  <I size={20} className="text-[#E8C87A] mb-3" />
                  <div className="font-display text-lg text-[#F5EFE0]">{c.t}</div>
                  <div className="text-xs text-[#F5EFE0]/55 mt-1">{c.d}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <Fleet />
      <TrustBand />
    </PageShell>
  );
}
